import useClassNames from 'helpers/hooks/useClassNames';
import { ButtonProps, ButtonSize, ButtonVariant } from '.';

type variantBasedClassName = { [key in ButtonVariant]?: string };
type IncludesIcon = { includesIcon: boolean };
type UseButtonClassNames = (
  props: Pick<ButtonProps, 'variant' | 'size' | 'className' | 'loading' | 'asSkeleton'> & IncludesIcon,
) => string;

const useButtonClassNames: UseButtonClassNames = ({
  variant,
  size,
  className = '',
  includesIcon,
  loading,
  asSkeleton = false,
}) => {
  const sizeClassNames: { [key in ButtonSize]: string } = {
    xs: 'p-8',
    s: 'py-12 px-24',
    m: 'py-14 px-36',
    l: 'py-16 px-48',
    fit: 'p-0',
    full: 'py-12 w-full',
    icon: 'w-40 h-40 grid items-center justify-center',
  };

  const primaryClassName = useClassNames([
    'bg-btnPrimary-active text-btnPrimary-activeText outline-offset-0 uppercase font-body font-semibold transition-color items-center',
    'hover:bg-btnPrimary-hover',
    'active:shadow-button active:disabled:shadow-none active:bg-btnPrimary-active',
    'focus-visible:outline focus-visible:outline-gray-700 border-gray-700 focus-visible:border focus-visible:outline-offset-[3px]',
    'disabled:bg-btnDisabled-active disabled:text-btnDisabled-text',
  ]);

  const secondaryClassName = useClassNames([
    'bg-btnSecondary-active text-btnSecondary-activeText rounded-md border border-secondary-black uppercase font-body font-semibold transition-color items-center',
    'hover:bg-btnSecondary-hover',
    'active:outline-2 active:shadow-button active:disabled:shadow-none active:outline-1 active:outline-gray-300 active:outline-offset-0',
    'focus-visible:outline focus-visible:border-white-100 focus-visible:border focus-visible:outline-offset-[3px]',
    'disabled:bg-btnDisabled-active disabled:text-btnDisabled-text',
  ]);

  const tertiaryClassName = useClassNames([
    'bg-btnTertiary-active text-btnTertiary-activeText uppercase font-body font-semibold transition-color items-center',
    'hover:bg-btnTertiary-hover hover:text-btnTertiary-hoverText',
    'active:outline-2 active:shadow-button active:disabled:shadow-none active:outline-1 active:outline-gray-300 active:outline-offset-0',
    'focus-visible:outline focus-visible:border-white-100 focus-visible:border focus-visible:outline-offset-[3px]',
    'disabled:bg-btnDisabled-active disabled:text-btnDisabled-text',
  ]);

  const warningClassName = useClassNames([
    'bg-red-500 text-neutral-100 outline-offset-0 uppercase font-body font-semibold transition-color items-center',
    'hover:bg-red-600',
    'active:shadow-button active:disabled:shadow-none active:bg-red-500',
    'focus-visible:outline focus-visible:bg-red-700 focus-visible:outline-gray-700 focus-visible:border-red-700 focus-visible:border focus-visible:outline-offset-[3px]',
    'disabled:bg-btnDisabled-active disabled:text-btnDisabled-text',
  ]);

  const underlineClassName =
    'pb-2 text-gray-700 hover:underline disabled:text-neutral-400 font-body font-semibold transition-color items-center';

  const ghostClassName = 'disabled:text-btnDisabled-text font-body font-semibold transition-color items-center';

  const variantClassNames: variantBasedClassName = {
    primary: primaryClassName,
    secondary: secondaryClassName,
    tertiary: tertiaryClassName,
    underlined: underlineClassName,
    ghost: ghostClassName,
    warning: warningClassName,
  };

  const buttonClassName = useClassNames([
    sizeClassNames[size as ButtonSize],
    { 'rounded-md': variant !== 'ghost' },
    { 'flex gap-10 items-center': includesIcon },
    { 'cursor-not-allowed bg-ag_green-500 text-black': !!loading },
    'relative text-14 leading-4 transition overflow-hidden',
    asSkeleton ? 'relative' : (variantClassNames[variant as ButtonVariant] as string),
    className,
  ]);

  return buttonClassName;
};

export default useButtonClassNames;
